// S19's landing (spec 15 §3.7 step 3, §4 S19; D-620): the console's one-time
// support link lands here on the tenant host as `/impersonate?code=…`. The
// code is read once, scrubbed from the address bar and posted to
// `POST /v1/auth/impersonation/redeem`, which sets the tenant session cookie;
// then the app opens fresh, with the banner. A code works once, for two
// minutes, on its own tenant, and every failure reads the same.
//
// `?ended=1` is where the banner's End lands: the session is gone.

import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { client } from "../lib/api";
import { resetProfileRead } from "../lib/profile-locale";
import { resetRefNames } from "../lib/ref-names";
import { resetRoleFeed } from "../lib/role-codes";
import { Button } from "../components/button";

type Phase = "redeeming" | "failed" | "missing" | "ended";

export function ImpersonationLanding() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const search = useSearch({ strict: false });
  const code = typeof search.code === "string" ? search.code : undefined;
  const ended = search.ended === "1";
  const [phase, setPhase] = useState<Phase>(
    ended ? "ended" : code === undefined ? "missing" : "redeeming",
  );
  // One redemption per page: the code is single use, and a second post (a
  // re-render, a development double effect) would read as a dead link.
  const started = useRef(false);

  useEffect(() => {
    if (ended || code === undefined || started.current) return;
    started.current = true;
    // The code never stays in the address bar or the history.
    window.history.replaceState(null, "", "/impersonate");
    void client
      .POST("/v1/auth/impersonation/redeem", { body: { code } })
      .then((result) => {
        if (!result.response.ok) {
          setPhase("failed");
          return;
        }
        // A session change forgets the names and roles read (D-441).
        resetProfileRead();
        resetRefNames(queryClient);
        resetRoleFeed(queryClient);
        // A full load: nothing cached from before this session may survive.
        window.location.assign("/home");
      })
      .catch(() => {
        setPhase("failed");
      });
  }, [code, ended]);

  if (phase === "redeeming") {
    return (
      <section className="auth-card" data-testid="impersonate-landing" aria-busy="true">
        <h1 className="auth-title">{t("admin.banner.landingTitle")}</h1>
      </section>
    );
  }
  const title =
    phase === "ended" ? t("admin.banner.endedTitle") : t("admin.banner.landingFailedTitle");
  const body =
    phase === "ended"
      ? t("admin.banner.endedBody")
      : phase === "missing"
        ? t("admin.banner.landingMissing")
        : t("admin.banner.landingFailedBody");
  return (
    <section className="auth-card" data-testid={`impersonate-${phase}`}>
      <h1 className="auth-title">{title}</h1>
      <p className="auth-lead">{body}</p>
      <Button
        variant="primary"
        onClick={() => {
          void navigate({ to: "/login" });
        }}
      >
        {t("admin.banner.goSignIn")}
      </Button>
    </section>
  );
}
